import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetAgentThreadsResponse,
  UserTodoItem,
  CreateUserTodoResponse,
} from '@dreamai/types';
import { AgentProvider } from '../../contexts/AgentContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import ThreadViewContainer from './sidebar/ThreadViewContainer';
import DreamieDisplay from './sidebar/DreamieDisplay';
import { ReactComponent as ChatBubblePlusIcon } from '../../icons/chat-bubble-plus.svg';
import { ReactComponent as ClosePanelIcon } from '../../icons/close-panel.svg';
import { ReactComponent as CloseXIcon } from '../../icons/close-x.svg';
import { ReactComponent as EndCallIcon } from '../../icons/webrtc-calls/end-call.svg';
import { ReactComponent as PhoneIcon } from '../../icons/phone.svg';
import AgentStatusPill from './canvas/AgentStatusPill';
import {
  useVoiceSession,
  ShowTodoParams,
  RemoveTodoParams,
  AnswerArtifactParams,
  ShowProjectParams,
  OpenEditorParams,
  PatchEditorParams,
  PatchEditorResult,
  CloseEditorParams,
} from '../../hooks/useVoiceSession';
import {
  ResponseArtifactDTO,
  UpdateResponseArtifactRequest,
  DesiredActionResponse,
} from '@dreamai/types';
import AudioRipple from '../AudioRipple';
import VoiceCallCardStack, {
  VoiceCallCard,
  VoiceCallEditorItem,
  VoiceCallProjectItem,
  OauthAccountOption,
} from './VoiceCallCardStack';
import { applyPatches, PatchOp } from '../../lib/editorPatch';
import SpendingApprovalModal, {
  CardSelection,
} from './sidebar/SpendingApprovalModal';
import { isOrganisationCard } from './sidebar/organisationSpend';
import { ReplyToItem } from '../../contexts/ReplyToContext';
import { SPENDING_ACTION_KEYS } from '../../lib/desiredActionFields';
import api from '../../api';
import './dreamie-avatar-button.scss';

export interface QuickLauncherOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Built-in action handled by the component */
  builtInAction?: 'chat' | 'call';
  /** Custom action callback */
  onSelect?: () => void;
  /** Keyboard shortcut key (single character). Shown as hint in menu and triggers action when menu is open. */
  shortcut?: string;
  /** Nested sub-options. When set, clicking this item expands to show children instead of triggering an action. */
  children?: QuickLauncherOption[];
}

interface DreamieAvatarButtonProps {
  /** Required when not using artifactId mode */
  agentId?: string;
  agentImageUrl?: string;
  agentNickname?: string;
  /** When provided, fetches/creates an artifact discussion thread instead of the main thread */
  artifactId?: string;
  /** Options to show in the quick launcher menu. If empty/undefined, opens thread directly. */
  options?: QuickLauncherOption[];
  /** Called with an optimistic UserTodoItem when a voice todo is created */
  onTodoCreated?: (todo: UserTodoItem) => void;
  /** Called when the API confirms the optimistic todo with the real ID */
  onTodoConfirmed?: (optimisticId: string, realId: string) => void;
  /** When true, tells the backend this is the user's first ever call — agent introduces itself and speaks first */
  isFirstCall?: boolean;
  /** When provided, launches a daily standup call with this standup ID */
  dailyStandupId?: string;
  /** Short teaser text from the rundown briefing for the pill */
  dailyStandupTeaser?: string;
  /** Called when a first call starts, so the parent can mark the milestone */
  onFirstCallStarted?: () => void;
  /** Called when a daily standup call starts, so the parent can clear the standup ID */
  onDailyStandupStarted?: () => void;
  /** Registers the startCall function so an external caller (e.g. mobile pill) can trigger it */
  onRegisterStartCall?: (fn: () => void) => void;
  /** When true, hides the idle avatar button (used on mobile where the pill replaces it) */
  hideIdleButton?: boolean;
  /** When true, hides the internal rainbow first-call / rundown-ready pills so the host can render them externally */
  hideIdleRainbowPills?: boolean;
  /** When provided (vh units), positions the call pill above the mobile bottom sheet */
  mobileSheetHeight?: number;
}

const findMainThread = (threads: { id: string; is_main_thread?: boolean }[]) =>
  threads.find((t) => t.is_main_thread);

const DreamieAvatarButton: React.FC<DreamieAvatarButtonProps> = ({
  agentId,
  agentImageUrl,
  agentNickname,
  artifactId,
  options,
  onTodoCreated,
  onTodoConfirmed,
  isFirstCall,
  dailyStandupId,
  dailyStandupTeaser,
  onFirstCallStarted,
  onDailyStandupStarted,
  onRegisterStartCall,
  hideIdleButton,
  hideIdleRainbowPills,
  mobileSheetHeight,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [mainThreadId, setMainThreadId] = useState<string | null>(null);
  const [resolvedAgentId, setResolvedAgentId] = useState<string | undefined>(
    agentId,
  );
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | undefined>(
    agentImageUrl,
  );
  const [resolvedNickname, setResolvedNickname] = useState<string | undefined>(
    agentNickname,
  );
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);
  const hasOpened = useRef(false);
  const hasMenuOpened = useRef(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { voiceCallingEnabled } = useFeatureFlags();
  // Voice calling is gated behind a single backend feature flag. Drop the
  // built-in "call" launcher option entirely when disabled so it never renders.
  const effectiveOptions = useMemo(
    () =>
      voiceCallingEnabled
        ? options
        : options?.filter((o) => o.builtInAction !== 'call'),
    [options, voiceCallingEnabled],
  );

  const hasOptions = effectiveOptions && effectiveOptions.length > 0;

  // --- Voice call state ---
  const [isInCall, setIsInCall] = useState(false);
  const [pendingCards, setPendingCards] = useState<VoiceCallCard[]>([]);
  const [showHangupWarning, setShowHangupWarning] = useState(false);
  const cardIdCounter = useRef(0);

  // --- Spending modal state ---
  const [showSpendApproval, setShowSpendApproval] = useState(false);
  const [spendApprovalItems, setSpendApprovalItems] = useState<ReplyToItem[]>(
    [],
  );
  const [spendApprovalCardId, setSpendApprovalCardId] = useState<string | null>(
    null,
  );
  const [spendIsDeposit, setSpendIsDeposit] = useState(false);

  const handleShowTodo = useCallback(
    (params: ShowTodoParams): string => {
      // Deduplication: check if a todo with the same description already exists on screen
      const descLower = params.description.toLowerCase().trim();
      const existingCard = pendingCards.find(
        (c) =>
          c.type === 'todo' && c.description.toLowerCase().trim() === descLower,
      );
      if (existingCard) {
        return 'DUPLICATE';
      }

      cardIdCounter.current += 1;
      const newCard: VoiceCallCard = {
        type: 'todo',
        id: `${cardIdCounter.current}`,
        description: params.description,
        actionKey: params.desired_action_key,
        actionLabel: params.desired_action_label,
        status: 'pending',
        spendDescription: params.spend_description,
        estimatedSpend: params.estimated_spend,
        spendCurrency: params.spend_currency,
      };
      setPendingCards((prev) => [...prev, newCard]);
      return newCard.id;
    },
    [pendingCards],
  );

  // Find a pending todo card by ID or description substring. Editor cards are
  // intentionally ignored here — they have their own remove pathway.
  const findPendingCard = useCallback(
    (todoId?: string, description?: string) => {
      const pending = pendingCards.filter(
        (c) => c.type === 'todo' && c.status === 'pending',
      );
      if (pending.length === 0) return undefined;

      if (todoId) {
        const card = pending.find((c) => c.id === todoId);
        if (card && card.type === 'todo') return card;
      }
      if (description) {
        const lower = description.toLowerCase();
        const card = pending.find(
          (c) =>
            c.type === 'todo' && c.description.toLowerCase().includes(lower),
        );
        if (card && card.type === 'todo') return card;
      }
      return undefined;
    },
    [pendingCards],
  );

  const handleAgentRemoveTodo = useCallback(
    (params: RemoveTodoParams): string => {
      const card = findPendingCard(params.todo_id, params.description);
      if (!card) return 'No matching pending todo found to remove.';

      setPendingCards((prev) => prev.filter((c) => c.id !== card.id));
      return `Todo "${card.description}" (id: ${card.id}) removed from screen.`;
    },
    [findPendingCard],
  );

  // --- Answer artifact state (for spend approval gating) ---
  const [artifactSpendPending, setArtifactSpendPending] = useState<{
    artifactId: string;
    body: UpdateResponseArtifactRequest;
    resolve: (value: string) => void;
  } | null>(null);

  const handleAnswerArtifact = useCallback(
    async (params: AnswerArtifactParams): Promise<string> => {
      const { artifact_id } = params;
      if (!artifact_id) return 'Missing artifact_id.';

      try {
        const token = await getAccessTokenSilently();

        // Fetch the artifact to know its type and spending details
        const fetchRes = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifact_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!fetchRes.data.success || !fetchRes.data.artifact) {
          return `Artifact ${artifact_id} not found.`;
        }

        const artifact = fetchRes.data.artifact;
        const artifactType = artifact.artifactType;

        // Build the desiredAction portion of userResponse
        let desiredAction: DesiredActionResponse | undefined;
        if (params.desired_action_key) {
          desiredAction = {
            key: params.desired_action_key as any,
            ...(params.desired_action_custom_text
              ? { customText: params.desired_action_custom_text }
              : {}),
            ...(params.clarifying_answers
              ? {
                  questionAnswers: JSON.parse(params.clarifying_answers),
                }
              : {}),
          };
          // Find the label from artifact's desired actions
          const allActions = [
            ...(artifact.desiredActions || []),
            ...(artifact.yesDesiredActions || []),
            ...(artifact.noDesiredActions || []),
          ];
          const matched = allActions.find(
            (a) => a.key === params.desired_action_key,
          );
          if (matched?.label) {
            desiredAction.label = matched.label;
          }
        }

        // Build userResponse based on artifact type
        let userResponse: any;
        if (artifactType === 'multiple_choice') {
          const choiceIds = params.selected_choice_ids
            ? params.selected_choice_ids.split(',').map((id) => id.trim())
            : [];
          userResponse = {
            selectedChoiceIds: choiceIds,
            ...(desiredAction ? { desiredAction } : {}),
          };
        } else if (artifactType === 'yes_or_no') {
          userResponse = {
            selection: params.yes_or_no || 'yes',
            ...(desiredAction ? { desiredAction } : {}),
          };
        } else if (artifactType === 'form_input') {
          const values = params.form_values
            ? JSON.parse(params.form_values)
            : {};
          userResponse = {
            values,
            ...(desiredAction ? { desiredAction } : {}),
          };
        } else if (artifactType === 'information_dump') {
          userResponse = { acknowledged: true };
        } else if (artifactType === 'spending_approval') {
          userResponse = { approved: true };
        } else if (artifactType === 'blank_todo') {
          userResponse = { completed: true };
        } else {
          userResponse = {};
        }

        // Check if spending approval is needed
        let needsSpendApproval = false;
        let spendAmount: number | undefined;
        let spendCurrency: string | undefined;
        let spendDescription: string | undefined;
        let isDeposit = false;

        // Check spend from choice prices (multiple_choice)
        if (
          artifactType === 'multiple_choice' &&
          artifact.choices &&
          userResponse.selectedChoiceIds?.length > 0
        ) {
          const selected = artifact.choices.filter((c: any) =>
            userResponse.selectedChoiceIds.includes(c.choiceId),
          );
          for (const choice of selected) {
            if (choice.definitivePrice != null) {
              spendAmount = (spendAmount || 0) + choice.definitivePrice;
              spendCurrency = choice.priceCurrency || 'GBP';
            } else if (choice.priceEstimate != null) {
              spendAmount = (spendAmount || 0) + choice.priceEstimate;
              spendCurrency = choice.priceCurrency || 'GBP';
            }
          }
          if (
            spendAmount &&
            desiredAction &&
            SPENDING_ACTION_KEYS.has(desiredAction.key)
          ) {
            needsSpendApproval = true;
            isDeposit = desiredAction.key === 'reserve';
            // Find spend description from the desired action
            const allActions = artifact.desiredActions || [];
            const matched = allActions.find(
              (a: any) => a.key === desiredAction!.key,
            );
            spendDescription = matched?.spendDescription;
          }
        }

        const body: UpdateResponseArtifactRequest = {
          artifactId: artifact_id,
          status: 'actioned',
          userResponse,
          source: 'chat',
          ...(needsSpendApproval && spendAmount && spendCurrency
            ? {
                create_mandate: true,
                mandate_items: [
                  {
                    actionLine: desiredAction?.label || 'Voice action',
                    descriptionLine: spendDescription
                      ? `Spend up to ${spendCurrency} ${spendAmount} on ${spendDescription}`
                      : `Spend up to ${spendCurrency} ${spendAmount}`,
                    price: spendAmount,
                    price_currency: spendCurrency,
                    isDeposit,
                  },
                ],
              }
            : {}),
        };

        // If spending approval needed, gate through the modal
        if (needsSpendApproval && spendAmount && spendCurrency) {
          const symbol =
            spendCurrency === 'GBP'
              ? '\u00a3'
              : spendCurrency === 'USD'
                ? '$'
                : spendCurrency === 'EUR'
                  ? '\u20ac'
                  : `${spendCurrency} `;

          return new Promise<string>((resolve) => {
            setSpendApprovalItems([
              {
                id: `voice-artifact-spend-${artifact_id}`,
                actionLine: desiredAction?.label || 'Voice action',
                descriptionLine: spendDescription
                  ? `${isDeposit ? 'Deposit' : 'Spend'} up to ${symbol}${spendAmount} on ${spendDescription}`
                  : `${isDeposit ? 'Deposit' : 'Spend'} up to ${symbol}${spendAmount}`,
                price: spendAmount!,
                price_currency: spendCurrency!,
                isDeposit,
                bookable: true,
              },
            ]);
            setSpendIsDeposit(isDeposit);
            setArtifactSpendPending({ artifactId: artifact_id, body, resolve });
            setShowSpendApproval(true);
          });
        }

        // No spending — submit directly
        await api.patch(`/response-artifacts/${artifact_id}`, body, {
          headers: { Authorization: `Bearer ${token}` },
        });

        return `Artifact "${artifact.title || artifact_id}" answered successfully (${artifactType}).`;
      } catch (err) {
        console.error('[DreamieAvatar] Failed to answer artifact:', err);
        return `Error answering artifact: ${err instanceof Error ? err.message : 'Unknown error'}`;
      }
    },
    [getAccessTokenSilently],
  );

  // ── Collaborative editor — agent-side handlers ────────────────────────
  // The agent uses open_editor / patch_editor / close_editor client tools.
  // They must be defined BEFORE useVoiceSession so we can pass them in
  // without a TDZ. They do not depend on voiceSession (the user-side
  // handlers below do, and receive it via closure after the hook runs).

  // Connected OAuth email accounts used by the email-draft save flow.
  // Lazily fetched the first time an email-draft editor opens so we don't
  // hit /accounts for every avatar mount.
  const [oauthAccounts, setOauthAccounts] = useState<OauthAccountOption[]>([]);
  const oauthAccountsFetchedRef = useRef(false);

  const fetchOauthAccounts = useCallback(async () => {
    if (oauthAccountsFetchedRef.current) return;
    oauthAccountsFetchedRef.current = true;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        accounts: Array<{ id: number; email: string }>;
      }>('/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOauthAccounts(
        res.data.accounts.map((a) => ({ id: a.id, email: a.email })),
      );
    } catch (err) {
      // Non-fatal: the picker will just show an empty dropdown and the
      // agent can still dictate into a document.
      console.error('[DreamieAvatar] Failed to fetch OAuth accounts:', err);
      oauthAccountsFetchedRef.current = false;
    }
  }, [getAccessTokenSilently]);

  const handleOpenEditor = useCallback(
    (params: OpenEditorParams): string => {
      // Deduplicate: the agent sometimes calls open_editor repeatedly during
      // a single turn; matching on (type + lowered title) is sufficient.
      const titleKey = (params.title || '').toLowerCase().trim();
      const existing = pendingCards.find(
        (c) =>
          c.type === 'editor' &&
          c.editorType === params.editor_type &&
          c.title.toLowerCase().trim() === titleKey,
      );
      if (existing) return 'DUPLICATE';

      cardIdCounter.current += 1;
      const newCard: VoiceCallEditorItem = {
        type: 'editor',
        id: `${cardIdCounter.current}`,
        editorType: params.editor_type,
        title: params.title || '',
        content: params.initial_content || '',
        status: 'editing',
        ...(params.editor_type === 'email_draft'
          ? {
              oauthId: params.oauth_id,
              to: params.to,
              cc: params.cc,
              bcc: params.bcc,
              subject: params.subject,
            }
          : {}),
      };
      setPendingCards((prev) => [...prev, newCard]);
      // Kick off the oauth accounts fetch so the picker is populated by
      // the time the user opens the email-draft dropdown.
      if (params.editor_type === 'email_draft') fetchOauthAccounts();
      return newCard.id;
    },
    [pendingCards, fetchOauthAccounts],
  );

  const handlePatchEditor = useCallback(
    (params: PatchEditorParams): PatchEditorResult => {
      const { editor_id, edits, title, to, cc, bcc, subject } = params;
      const card = pendingCards.find(
        (c) => c.id === editor_id && c.type === 'editor',
      ) as VoiceCallEditorItem | undefined;
      if (!card) {
        return {
          summary: `No editor on screen with id "${editor_id}". Call open_editor first (and pass initial_content so the body isn't empty).`,
          severity: 'error',
        };
      }
      if (card.status !== 'editing') {
        return {
          summary: `Editor ${editor_id} is no longer editable (status: ${card.status}).`,
          severity: 'error',
        };
      }
      const {
        content: nextContent,
        errors,
        appliedCount,
      } = applyPatches(card.content, edits as PatchOp[]);
      // Build the per-card patch from provided fields only so undefined keys
      // don't overwrite existing values via spread.
      const fieldPatch: Partial<VoiceCallEditorItem> = { content: nextContent };
      const appliedFields: string[] = [];
      if (title !== undefined) {
        fieldPatch.title = title;
        appliedFields.push('title');
      }
      if (to !== undefined) {
        fieldPatch.to = to;
        appliedFields.push('to');
      }
      if (cc !== undefined) {
        fieldPatch.cc = cc;
        appliedFields.push('cc');
      }
      if (bcc !== undefined) {
        fieldPatch.bcc = bcc;
        appliedFields.push('bcc');
      }
      if (subject !== undefined) {
        fieldPatch.subject = subject;
        appliedFields.push('subject');
      }
      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === editor_id && c.type === 'editor'
            ? ({ ...c, ...fieldPatch } as VoiceCallEditorItem)
            : c,
        ),
      );
      // Complete failure: every op missed its anchor AND no header fields
      // were updated. The agent must NOT speak as if the body changed —
      // surface this as severity='error' so the wrapper emits [ERROR].
      const allOpsFailed =
        edits.length > 0 && appliedCount === 0 && appliedFields.length === 0;
      const parts: string[] = [];
      if (allOpsFailed) {
        const wasEmpty = card.content.length === 0;
        parts.push(
          wasEmpty
            ? `❌ Applied 0/${edits.length} edits — the editor body is still EMPTY. The ops you sent (replace/insert_*/delete) need existing text to anchor on. Retry with {op:"set", text:"<full body>"} OR re-open with initial_content.`
            : `❌ Applied 0/${edits.length} edits — your find/anchor strings did not match the current body. Re-read the body (sent on the next turn) and retry with exact substrings, or use {op:"set", text:"<full body>"} to rewrite from scratch.`,
        );
      } else {
        parts.push(
          `Applied ${appliedCount}/${edits.length} edit(s) to editor ${editor_id}. Document is now ${nextContent.length} chars.`,
        );
      }
      if (appliedFields.length > 0) {
        parts.push(`Updated fields: ${appliedFields.join(', ')}.`);
      }
      if (errors.length > 0) {
        parts.push(`Errors: ${errors.join(' | ')}`);
      }
      return {
        summary: parts.join(' '),
        severity: allOpsFailed ? 'error' : 'ok',
      };
    },
    [pendingCards],
  );

  // Shared debounce-timer cleanup used by handleCloseEditor (agent-initiated)
  // and handleEditorRemove (user-initiated). Declared as a ref so it can be
  // safely referenced before editorPushTimersRef is initialised — the ref
  // object itself is created lower in the component but the map is always
  // present.
  const editorPushTimersRef = useRef<Map<string, number>>(new Map());

  const handleShowProject = useCallback(
    (params: ShowProjectParams): string => {
      const nameLower = params.project_name.toLowerCase().trim();
      const existing = pendingCards.find(
        (c) =>
          c.type === 'project' &&
          c.projectName.toLowerCase().trim() === nameLower,
      );
      if (existing) return 'DUPLICATE';

      cardIdCounter.current += 1;
      const newCard: VoiceCallProjectItem = {
        type: 'project',
        id: `${cardIdCounter.current}`,
        projectName: params.project_name,
        direction: params.direction,
        status: 'pending',
      };
      setPendingCards((prev) => [...prev, newCard]);
      return newCard.id;
    },
    [pendingCards],
  );

  const handleCloseEditor = useCallback(
    (params: CloseEditorParams): string => {
      const { editor_id } = params;
      const card = pendingCards.find(
        (c) => c.id === editor_id && c.type === 'editor',
      );
      if (!card) return `No editor with id "${editor_id}" to close.`;
      setPendingCards((prev) => prev.filter((c) => c.id !== editor_id));
      // Cancel any pending push-back for this card so it doesn't fire after
      // we've closed the editor.
      const timer = editorPushTimersRef.current.get(editor_id);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        editorPushTimersRef.current.delete(editor_id);
      }
      return `Editor ${editor_id} closed.`;
    },
    [pendingCards],
  );

  const voiceSession = useVoiceSession({
    agentId,
    isFirstCall,
    dailyStandupId: dailyStandupId || undefined,
    onConnectionChange: (connected) => {
      if (!connected && isInCall) {
        // Call disconnected — exit call mode
        setIsInCall(false);
      }
    },
    onError: (err) => {
      console.error('[DreamieAvatar] Voice error:', err);
    },
    onShowTodo: handleShowTodo,
    onRemoveTodo: handleAgentRemoveTodo,
    onAnswerArtifact: handleAnswerArtifact,
    onShowProject: handleShowProject,
    onOpenEditor: handleOpenEditor,
    onPatchEditor: handlePatchEditor,
    onCloseEditor: handleCloseEditor,
  });

  // Actually submit the todo to the API (called directly or after spend approval)
  const doCreateTodo = useCallback(
    async (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      if (!card || card.type !== 'todo') return;

      // Mark as creating — guard the type narrowing since the union also
      // contains editor cards which have a different `status` shape.
      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === cardId && c.type === 'todo'
            ? { ...c, status: 'creating' as const }
            : c,
        ),
      );

      // Build optimistic todo for the CentralTodoList
      const optimisticId = `optimistic-${Date.now()}`;
      const optimisticTodo: UserTodoItem = {
        id: optimisticId,
        description: card.description,
        status: 'pending',
        assigneeType: 'agent',
        agentId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      onTodoCreated?.(optimisticTodo);

      // Build mandate items if this card has spend info
      const mandateItems =
        card.estimatedSpend && card.spendCurrency
          ? [
              {
                actionLine: card.actionLabel,
                descriptionLine: card.spendDescription
                  ? `Spend up to ${card.spendCurrency === 'GBP' ? '\u00a3' : card.spendCurrency === 'USD' ? '$' : card.spendCurrency === 'EUR' ? '\u20ac' : `${card.spendCurrency} `}${card.estimatedSpend} on ${card.spendDescription}`
                  : `Spend up to ${card.spendCurrency === 'GBP' ? '\u00a3' : card.spendCurrency === 'USD' ? '$' : card.spendCurrency === 'EUR' ? '\u20ac' : `${card.spendCurrency} `}${card.estimatedSpend}`,
                price: parseFloat(card.estimatedSpend),
                price_currency: card.spendCurrency,
                isDeposit: card.actionKey === 'reserve',
              },
            ]
          : undefined;

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<CreateUserTodoResponse>(
          '/user-todos',
          {
            description: card.description,
            assigneeType: 'agent',
            agentId,
            selectedActionKey: card.actionKey,
            selectedActionLabel: card.actionLabel,
            ...(mandateItems ? { mandateItems } : {}),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data.tempTodo?.id) {
          onTodoConfirmed?.(optimisticId, response.data.tempTodo.id);
        }

        // Mark as created (todo-only)
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'todo'
              ? { ...c, status: 'created' as const }
              : c,
          ),
        );

        // Notify the agent
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user confirmed and created a todo: "${card.description}" with action "${card.actionKey}". Acknowledge briefly.`,
        );
      } catch (err) {
        console.error('[DreamieAvatar] Failed to create todo:', err);
        // Revert to pending on failure (todo-only)
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'todo'
              ? { ...c, status: 'pending' as const }
              : c,
          ),
        );
      }
    },
    [
      pendingCards,
      getAccessTokenSilently,
      voiceSession,
      agentId,
      onTodoCreated,
      onTodoConfirmed,
    ],
  );

  // Gate: if the card has spend info, show spending approval modal first
  const handleCreateTodo = useCallback(
    (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      if (!card || card.type !== 'todo') return;

      const hasSpend =
        card.estimatedSpend &&
        card.spendCurrency &&
        SPENDING_ACTION_KEYS.has(card.actionKey);

      if (hasSpend) {
        const amount = parseFloat(card.estimatedSpend!);
        const isDeposit = card.actionKey === 'reserve';
        const currency = card.spendCurrency!;
        const symbol =
          currency === 'GBP'
            ? '\u00a3'
            : currency === 'USD'
              ? '$'
              : currency === 'EUR'
                ? '\u20ac'
                : `${currency} `;

        setSpendApprovalItems([
          {
            id: `voice-spend-${cardId}`,
            actionLine: card.actionLabel,
            descriptionLine: card.spendDescription
              ? `${isDeposit ? 'Deposit' : 'Spend'} up to ${symbol}${card.estimatedSpend} on ${card.spendDescription}`
              : `${isDeposit ? 'Deposit' : 'Spend'} up to ${symbol}${card.estimatedSpend}`,
            price: amount,
            price_currency: currency,
            isDeposit,
            bookable: true,
          },
        ]);
        setSpendIsDeposit(isDeposit);
        setSpendApprovalCardId(cardId);
        setShowSpendApproval(true);
      } else {
        doCreateTodo(cardId);
      }
    },
    [pendingCards, doCreateTodo],
  );

  const handleRemoveTodo = useCallback(
    (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      setPendingCards((prev) => prev.filter((c) => c.id !== cardId));
      if (card && card.type === 'todo') {
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user removed/dismissed the todo: "${card.description}". Acknowledge briefly.`,
        );
      }
    },
    [pendingCards, voiceSession],
  );

  const doStartProject = useCallback(
    async (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      if (!card || card.type !== 'project') return;

      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === cardId && c.type === 'project'
            ? { ...c, status: 'creating' as const }
            : c,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        const res = await api.post<{
          success: boolean;
          threadId: string;
          projectName: string;
        }>(
          '/agents/start-project-from-quick-ask',
          {
            projectName: card.projectName,
            projectBriefing: card.direction,
            agent_id: agentId,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'project'
              ? { ...c, status: 'created' as const }
              : c,
          ),
        );

        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user confirmed and started the project "${card.projectName}". A new project thread has been created (thread ${res.data.threadId}). Acknowledge briefly.`,
        );
      } catch (err) {
        console.error('[DreamieAvatar] Failed to start project:', err);
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'project'
              ? { ...c, status: 'pending' as const }
              : c,
          ),
        );
      }
    },
    [pendingCards, getAccessTokenSilently, voiceSession, agentId],
  );

  const handleRemoveProject = useCallback(
    (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      setPendingCards((prev) => prev.filter((c) => c.id !== cardId));
      if (card && card.type === 'project') {
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user dismissed the project card "${card.projectName}". They do not want to start this project right now. Acknowledge briefly.`,
        );
      }
    },
    [pendingCards, voiceSession],
  );

  // ── Collaborative editor — user-side handlers ─────────────────────────
  // React to user activity in an editor card (typing, field changes, save,
  // remove). All of these use voiceSession so they live below the
  // useVoiceSession() call above. Agent-side handlers (open/patch/close)
  // are defined earlier in the component so they can be passed INTO the
  // hook without a TDZ.

  // `editorPushTimersRef` is declared earlier (next to handleCloseEditor)
  // so both the agent-initiated close and the user-initiated remove paths
  // can share the same timer map.

  /**
   * Build a terse contextual update describing the editor's current state,
   * so the agent stays in sync with what the user has typed. Debounced on
   * user edits; flushed immediately on save.
   */
  const buildEditorUpdateMessage = useCallback(
    (card: VoiceCallEditorItem): string => {
      const headerParts: string[] = [];
      if (card.editorType === 'email_draft') {
        if (card.to) headerParts.push(`To: ${card.to}`);
        if (card.cc) headerParts.push(`Cc: ${card.cc}`);
        if (card.bcc) headerParts.push(`Bcc: ${card.bcc}`);
        if (card.subject) headerParts.push(`Subject: ${card.subject}`);
      }
      const header =
        headerParts.length > 0 ? `${headerParts.join(' | ')}\n` : '';
      return `[editor ${card.id} — ${card.editorType}] user edited. Current title: "${card.title}"\n${header}---\n${card.content}\n---`;
    },
    [],
  );

  const flushEditorPush = useCallback(
    (cardId: string) => {
      const timer = editorPushTimersRef.current.get(cardId);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        editorPushTimersRef.current.delete(cardId);
      }
      // Read fresh card from state via setter trick — functional-update ref.
      setPendingCards((prev) => {
        const card = prev.find((c) => c.id === cardId);
        if (card && card.type === 'editor') {
          voiceSession.sendContextualUpdate(buildEditorUpdateMessage(card));
        }
        return prev;
      });
    },
    [voiceSession, buildEditorUpdateMessage],
  );

  const scheduleEditorPush = useCallback(
    (cardId: string) => {
      const existing = editorPushTimersRef.current.get(cardId);
      if (existing !== undefined) window.clearTimeout(existing);
      const handle = window.setTimeout(() => {
        flushEditorPush(cardId);
      }, 800);
      editorPushTimersRef.current.set(cardId, handle);
    },
    [flushEditorPush],
  );

  const handleEditorContentChange = useCallback(
    (cardId: string, content: string) => {
      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === cardId && c.type === 'editor' ? { ...c, content } : c,
        ),
      );
      scheduleEditorPush(cardId);
    },
    [scheduleEditorPush],
  );

  const handleEditorFieldChange = useCallback(
    (
      cardId: string,
      field: 'title' | 'to' | 'cc' | 'bcc' | 'subject' | 'oauthId',
      value: string | number,
    ) => {
      setPendingCards((prev) =>
        prev.map((c) => {
          if (c.id !== cardId || c.type !== 'editor') return c;
          // Type-narrow assignment to keep editor status/field validity.
          if (field === 'oauthId') {
            return { ...c, oauthId: Number(value) };
          }
          return { ...c, [field]: String(value) };
        }),
      );
      scheduleEditorPush(cardId);
    },
    [scheduleEditorPush],
  );

  const handleEditorRemove = useCallback(
    (cardId: string) => {
      const card = pendingCards.find((c) => c.id === cardId);
      setPendingCards((prev) => prev.filter((c) => c.id !== cardId));
      const timer = editorPushTimersRef.current.get(cardId);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        editorPushTimersRef.current.delete(cardId);
      }
      if (card && card.type === 'editor') {
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user dismissed the ${card.editorType} editor "${card.title}" without saving.`,
        );
      }
    },
    [pendingCards, voiceSession],
  );

  const handleSaveDocument = useCallback(
    async (cardId: string) => {
      const card = pendingCards.find(
        (c) => c.id === cardId && c.type === 'editor',
      ) as VoiceCallEditorItem | undefined;
      if (!card) return;

      // Flush any pending push so the agent sees the final content at save time.
      flushEditorPush(cardId);

      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === cardId && c.type === 'editor'
            ? { ...c, status: 'saving', saveError: undefined }
            : c,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        const res = await api.post<{
          success: boolean;
          artifactId: string;
          appId: string;
          error?: string;
        }>(
          '/editor/save-document',
          {
            title: card.title || 'Untitled document',
            content: card.content,
            agentId,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.data.success) {
          throw new Error(res.data.error || 'Save failed');
        }
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'editor'
              ? {
                  ...c,
                  status: 'saved',
                  savedArtifactId: res.data.artifactId,
                }
              : c,
          ),
        );
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user saved the document "${card.title}" to the Documents tab (artifact ${res.data.artifactId}). Acknowledge briefly.`,
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[DreamieAvatar] save-document failed:', err);
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'editor'
              ? { ...c, status: 'error', saveError: message }
              : c,
          ),
        );
      }
    },
    [
      pendingCards,
      getAccessTokenSilently,
      voiceSession,
      agentId,
      flushEditorPush,
    ],
  );

  const handleSaveEmailDraft = useCallback(
    async (cardId: string) => {
      const card = pendingCards.find(
        (c) => c.id === cardId && c.type === 'editor',
      ) as VoiceCallEditorItem | undefined;
      if (!card) return;
      if (!card.oauthId) {
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'editor'
              ? {
                  ...c,
                  status: 'error',
                  saveError: 'Pick an account to save the draft in.',
                }
              : c,
          ),
        );
        return;
      }

      flushEditorPush(cardId);

      setPendingCards((prev) =>
        prev.map((c) =>
          c.id === cardId && c.type === 'editor'
            ? { ...c, status: 'saving', saveError: undefined }
            : c,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        const res = await api.post<{
          success: boolean;
          draftId: string;
          gmailUrl?: string;
          error?: string;
        }>(
          '/editor/save-email-draft',
          {
            oauthId: card.oauthId,
            to: card.to || '',
            cc: card.cc || '',
            bcc: card.bcc || '',
            subject: card.subject || card.title || '(no subject)',
            content: card.content,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.data.success) {
          throw new Error(res.data.error || 'Save failed');
        }
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'editor'
              ? {
                  ...c,
                  status: 'saved',
                  savedDraftId: res.data.draftId,
                  gmailUrl: res.data.gmailUrl,
                }
              : c,
          ),
        );
        voiceSession.sendContextualUpdate(
          `[USER ACTION] The user saved the email draft "${card.subject || card.title}" as a Gmail draft in their account. Acknowledge briefly.`,
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('[DreamieAvatar] save-email-draft failed:', err);
        setPendingCards((prev) =>
          prev.map((c) =>
            c.id === cardId && c.type === 'editor'
              ? { ...c, status: 'error', saveError: message }
              : c,
          ),
        );
        // Tell the voice agent the save failed so it doesn't claim success.
        // The success path sends an analogous [USER ACTION] update; without
        // this the agent silently believes the draft was saved.
        voiceSession.sendContextualUpdate(
          `[SYSTEM] Saving the email draft "${card.subject || card.title}" to Gmail FAILED. Error: ${message}. Tell the user the save did not go through and offer to retry or adjust the draft.`,
        );
      }
    },
    [pendingCards, getAccessTokenSilently, voiceSession, flushEditorPush],
  );

  const startCall = useCallback(async () => {
    // Voice calling is gated behind a single backend feature flag.
    if (!voiceCallingEnabled) return;
    // Close menu
    setShowMenu(false);
    setIsMenuClosing(false);
    // Enter call mode
    setIsInCall(true);
    setPendingCards([]);
    if (isFirstCall) onFirstCallStarted?.();
    if (dailyStandupId) onDailyStandupStarted?.();
    await voiceSession.start();
  }, [
    voiceCallingEnabled,
    voiceSession,
    isFirstCall,
    dailyStandupId,
    onFirstCallStarted,
    onDailyStandupStarted,
  ]);

  useEffect(() => {
    onRegisterStartCall?.(() => startCall());
  }, [startCall, onRegisterStartCall]);

  const endCall = useCallback(async () => {
    // Check for uncreated todos
    const uncreated = pendingCards.filter(
      (c) => c.type === 'todo' && c.status !== 'created',
    );
    if (uncreated.length > 0 && !showHangupWarning) {
      setShowHangupWarning(true);
      return;
    }
    setShowHangupWarning(false);
    setPendingCards([]);
    await voiceSession.stop();
    setIsInCall(false);
  }, [voiceSession, pendingCards, showHangupWarning]);

  const dismissHangupWarning = useCallback(() => {
    setShowHangupWarning(false);
  }, []);

  // Fetch thread data (called on mount when no options, or on-demand when "chat" is selected)
  const fetchThread = useCallback(async () => {
    if (!agentId && !artifactId) return;
    if (mainThreadId) return; // Already fetched
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();

      if (artifactId) {
        // Artifact mode: find or create an artifact discussion thread
        const response = await api.post(
          `/response-artifacts/${artifactId}/thread`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.success) {
          setMainThreadId(response.data.threadId);
          if (response.data.agentId) {
            setResolvedAgentId(response.data.agentId);
          }
          if (response.data.agentImageUrl) {
            setResolvedImageUrl(response.data.agentImageUrl);
          }
          if (response.data.agentNickname) {
            setResolvedNickname(response.data.agentNickname);
          }
        }
      } else if (agentId) {
        // Default mode: fetch the agent's main thread
        const response = await api.get<GetAgentThreadsResponse>(
          '/agents/threads',
          {
            params: {
              agent_id: agentId,
              excludePinned: 'true',
              excludeShared: 'true',
              excludeSubThreads: 'true',
            },
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          const thread = findMainThread(response.data.threads);
          setMainThreadId(thread?.id ?? null);
        }
      }
    } catch (err) {
      console.error('Error fetching thread:', err);
    } finally {
      setLoading(false);
    }
  }, [agentId, artifactId, getAccessTokenSilently, mainThreadId]);

  // Fetch thread eagerly on mount when:
  // - No options menu (legacy mode), OR
  // - artifactId is provided (need agent details for avatar even in options mode)
  useEffect(() => {
    if (!hasOptions || artifactId) {
      fetchThread();
    }
  }, [hasOptions, artifactId, fetchThread]);

  const handleClosePanel = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setShowPanel(false);
      setIsClosing(false);
    }, 300);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsMenuClosing(true);
    setExpandedOptionId(null);
    setTimeout(() => {
      setShowMenu(false);
      setIsMenuClosing(false);
    }, 200);
  }, []);

  // Open the chat panel (used by the "chat" built-in action and legacy mode)
  const openChatPanel = useCallback(async () => {
    // Close menu first if it's open
    if (showMenu) {
      setShowMenu(false);
      setIsMenuClosing(false);
    }
    // Fetch thread if not yet loaded
    if (!mainThreadId) {
      await fetchThread();
    }
    hasOpened.current = true;
    setShowPanel(true);
  }, [showMenu, mainThreadId, fetchThread]);

  const handleOptionSelect = useCallback(
    (option: QuickLauncherOption) => {
      if (option.builtInAction === 'chat') {
        openChatPanel();
      } else if (option.builtInAction === 'call') {
        startCall();
      } else if (option.onSelect) {
        handleCloseMenu();
        option.onSelect();
      }
    },
    [openChatPanel, startCall, handleCloseMenu],
  );

  const handleToggle = useCallback(() => {
    // If chat panel is open, close it
    if (showPanel && !isClosing) {
      handleClosePanel();
      return;
    }
    // If menu is open, close it
    if (showMenu && !isMenuClosing) {
      handleCloseMenu();
      return;
    }
    // If we have options, show the menu (but not if it's currently closing)
    if (hasOptions && !showPanel && !isMenuClosing) {
      hasMenuOpened.current = true;
      setShowMenu(true);
      return;
    }
    // Legacy mode: open chat directly (only when no options menu)
    if (!hasOptions && !showPanel) {
      hasOpened.current = true;
      setShowPanel(true);
    }
  }, [
    showPanel,
    showMenu,
    isClosing,
    isMenuClosing,
    hasOptions,
    handleClosePanel,
    handleCloseMenu,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isInCall) {
          endCall();
        } else if (showPanel && !isClosing) {
          handleClosePanel();
        } else if (showMenu && !isMenuClosing) {
          handleCloseMenu();
        }
      }

      // Keyboard shortcuts — work globally unless typing in an input
      if (!isInCall && !showPanel && !isClosing && effectiveOptions) {
        // Ignore when modifier keys are held (Cmd+C, Ctrl+C, etc.)
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        const tag = (e.target as HTMLElement)?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          (e.target as HTMLElement)?.isContentEditable ||
          (e.target as HTMLElement)?.getAttribute('role') === 'textbox';
        if (!isEditable) {
          const key = e.key.toLowerCase();
          const matched = effectiveOptions.find(
            (o) => o.shortcut && o.shortcut.toLowerCase() === key,
          );
          if (matched) {
            e.preventDefault();
            handleOptionSelect(matched);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isInCall,
    endCall,
    showPanel,
    showMenu,
    isClosing,
    isMenuClosing,
    handleClosePanel,
    handleCloseMenu,
    effectiveOptions,
    handleOptionSelect,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        handleCloseMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu, handleCloseMenu]);

  // In legacy (no options) mode, hide until thread is loaded
  if (!hasOptions && (loading || !mainThreadId || !resolvedAgentId))
    return null;

  const isPanelOpen = showPanel && !isClosing;
  const isMenuOpen = showMenu && !isMenuClosing;

  return (
    <div className='dreamie-avatar-wrapper'>
      {/* Full-screen overlay when in call */}
      {isInCall && <div className='dreamie-call-overlay' />}

      {/* Voice call card stack — centered on screen */}
      {isInCall && pendingCards.length > 0 && (
        <VoiceCallCardStack
          cards={pendingCards}
          agentNickname={resolvedNickname}
          onCreateTodo={handleCreateTodo}
          onRemoveTodo={handleRemoveTodo}
          onEditorContentChange={handleEditorContentChange}
          onEditorFieldChange={handleEditorFieldChange}
          onEditorRemove={handleEditorRemove}
          onSaveDocument={handleSaveDocument}
          onSaveEmailDraft={handleSaveEmailDraft}
          onStartProject={doStartProject}
          onRemoveProject={handleRemoveProject}
          oauthAccounts={oauthAccounts}
        />
      )}

      {/* Hangup warning dialog */}
      {showHangupWarning && (
        <div className='voice-hangup-warning'>
          <p>You have uncreated todos. Hang up anyway?</p>
          <div className='voice-hangup-warning-actions'>
            <button
              className='voice-hangup-warning-cancel'
              onClick={dismissHangupWarning}
            >
              Go Back
            </button>
            <button
              className='voice-hangup-warning-confirm'
              onClick={() => {
                setShowHangupWarning(false);
                setPendingCards([]);
                voiceSession.stop().then(() => setIsInCall(false));
              }}
            >
              Hang Up
            </button>
          </div>
        </div>
      )}

      {/* Hang-up button — above the avatar during a call (desktop) */}
      {isInCall && (
        <button className='dreamie-hangup-btn' onClick={endCall}>
          <EndCallIcon />
        </button>
      )}

      {/* Mobile in-call UI — centered pill with hangup + avatar */}
      {isInCall && (
        <div className='dreamie-mobile-call-ui'>
          <span className='mobile-call-status'>
            {voiceSession.status === 'connecting'
              ? 'Connecting...'
              : voiceSession.isSpeaking
                ? 'Speaking'
                : voiceSession.isThinking
                  ? 'Thinking...'
                  : 'Listening'}
          </span>
          <div className='mobile-call-pill'>
            <button className='mobile-hangup-btn' onClick={endCall}>
              <CloseXIcon />
            </button>
            <div className='mobile-call-avatar'>
              {resolvedImageUrl && (
                <img
                  src={resolvedImageUrl}
                  alt={resolvedNickname || 'Secretary'}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* First call pill — always visible, shifts up when menu opens */}
      {voiceCallingEnabled &&
        !hideIdleRainbowPills &&
        isFirstCall &&
        !isInCall &&
        !isPanelOpen && (
          <button
            className={`dreamie-first-call-pill${isMenuOpen ? ' menu-open' : ''}`}
            style={
              mobileSheetHeight != null
                ? { bottom: `calc(${mobileSheetHeight}vh + 12px)` }
                : undefined
            }
            onClick={startCall}
          >
            <PhoneIcon />
            <span>Call me!</span>
          </button>
        )}

      {/* Daily rundown pill — shown when a rundown is ready */}
      {voiceCallingEnabled &&
        !hideIdleRainbowPills &&
        dailyStandupId &&
        !isFirstCall &&
        !isInCall &&
        !isPanelOpen && (
          <button
            className={`dreamie-first-call-pill${isMenuOpen ? ' menu-open' : ''}`}
            style={
              mobileSheetHeight != null
                ? { bottom: `calc(${mobileSheetHeight}vh + 12px)` }
                : undefined
            }
            onClick={startCall}
          >
            <PhoneIcon />
            <span>{'Rundown Ready'}</span>
          </button>
        )}

      {/* Options menu */}
      {hasOptions && hasMenuOpened.current && !isInCall && (
        <div
          ref={menuRef}
          className={`dreamie-options-menu${isMenuOpen ? ' open' : ''}${isMenuClosing ? ' closing' : ''}`}
        >
          {expandedOptionId ? (
            <>
              {effectiveOptions
                ?.find((o) => o.id === expandedOptionId)
                ?.children?.map((child) => (
                  <button
                    key={child.id}
                    className='dreamie-option-item'
                    onClick={() => handleOptionSelect(child)}
                  >
                    <span className='dreamie-option-label'>{child.label}</span>
                  </button>
                ))}
              <button
                className='dreamie-option-item back-btn'
                onClick={() => setExpandedOptionId(null)}
              >
                <span className='dreamie-option-label'>&#8249; Back</span>
              </button>
            </>
          ) : (
            effectiveOptions?.map((option) => (
              <button
                key={option.id}
                className='dreamie-option-item'
                onClick={
                  option.children
                    ? () => setExpandedOptionId(option.id)
                    : () => handleOptionSelect(option)
                }
              >
                <span className='dreamie-option-label'>{option.label}</span>
                {option.shortcut && (
                  <span className='dreamie-option-shortcut'>
                    (
                    {option.shortcut === ' '
                      ? 'SPACE'
                      : option.shortcut.toUpperCase()}
                    )
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {/* Chat panel */}
      {hasOpened.current && (
        <div
          className={`dreamie-chat-panel${isPanelOpen ? ' open' : ''}${isClosing ? ' closing' : ''}`}
        >
          <div className='dreamie-chat-header'>
            <span>{resolvedNickname || 'Secretary'}</span>
            <button className='dreamie-chat-close' onClick={handleClosePanel}>
              <ClosePanelIcon />
            </button>
          </div>
          {mainThreadId && resolvedAgentId && (
            <AgentProvider>
              <AgentStatusPill threadId={mainThreadId} />
              <div className='dreamie-chat-body'>
                <ThreadViewContainer
                  threadId={mainThreadId}
                  agentId={resolvedAgentId}
                  inMiniMode={true}
                />
                <DreamieDisplay agentId={resolvedAgentId} />
              </div>
            </AgentProvider>
          )}
          {loading && (
            <div className='dreamie-chat-loading'>
              <span>Loading...</span>
            </div>
          )}
        </div>
      )}

      {/* Main avatar button — grows 2x when in call */}
      <button
        className={`dreamie-avatar-btn${isPanelOpen || isMenuOpen ? ' active' : ''}${isInCall ? ' in-call' : ''}`}
        onClick={isInCall ? undefined : handleToggle}
        style={
          hideIdleButton && !isInCall
            ? { display: 'none' }
            : isInCall
              ? { cursor: 'default' }
              : undefined
        }
      >
        {resolvedImageUrl ? (
          <img
            className={`avatar-image${!isInCall && (isPanelOpen || isMenuOpen) ? ' hidden' : ''}`}
            src={resolvedImageUrl}
            alt={resolvedNickname || 'Secretary'}
          />
        ) : (
          !isInCall && !(isPanelOpen || isMenuOpen) && <ChatBubblePlusIcon />
        )}
        {!isInCall && (isPanelOpen || isMenuOpen) && (
          <CloseXIcon className='close-x-icon' />
        )}
        {!isInCall && !(isPanelOpen || isMenuOpen) && (
          <div className='dreamie-avatar-badge'>
            <ChatBubblePlusIcon />
          </div>
        )}
      </button>

      {/* Waveform — rendered as sibling so button overflow:hidden doesn't clip it */}
      {isInCall && (
        <AudioRipple
          outputLevel={voiceSession.outputLevel}
          isSpeaking={voiceSession.isSpeaking}
          outputLevelRef={voiceSession.outputLevelRef}
          size={212}
        />
      )}

      {/* Status label below avatar when in call */}
      {isInCall && (
        <span className='dreamie-call-status'>
          {voiceSession.status === 'connecting'
            ? 'Connecting...'
            : voiceSession.isSpeaking
              ? 'Speaking'
              : voiceSession.isThinking
                ? 'Thinking...'
                : 'Listening'}
        </span>
      )}

      {/* Spending approval modal for voice todos */}
      <SpendingApprovalModal
        show={showSpendApproval}
        onApprove={async (cardSel?: CardSelection) => {
          // Organisation-funded spend is charged on submit and may be
          // declined, so the sheet stays open until the submit resolves.
          const keepSheetOpen =
            isOrganisationCard(cardSel) && artifactSpendPending != null;
          if (!keepSheetOpen) setShowSpendApproval(false);
          if (artifactSpendPending) {
            // Artifact spend flow — submit the artifact answer
            const { artifactId, body, resolve } = artifactSpendPending;
            try {
              const token = await getAccessTokenSilently();
              const patchBody = cardSel
                ? { ...body, card_selection: cardSel }
                : body;
              await api.patch(`/response-artifacts/${artifactId}`, patchBody, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setArtifactSpendPending(null);
              setShowSpendApproval(false);
              resolve(`Artifact answered successfully with spending approved.`);
            } catch (err) {
              if (keepSheetOpen) throw err;
              console.error(
                '[DreamieAvatar] Failed to submit artifact after spend approval:',
                err,
              );
              setArtifactSpendPending(null);
              resolve(
                `Error submitting artifact: ${err instanceof Error ? err.message : 'Unknown error'}`,
              );
            }
          } else if (spendApprovalCardId) {
            doCreateTodo(spendApprovalCardId);
            setSpendApprovalCardId(null);
          }
        }}
        onCancel={() => {
          setShowSpendApproval(false);
          if (artifactSpendPending) {
            artifactSpendPending.resolve(
              'User cancelled spending approval. The artifact was not submitted.',
            );
            setArtifactSpendPending(null);
          } else {
            setSpendApprovalCardId(null);
          }
        }}
        items={spendApprovalItems}
        agentImageUrl={resolvedImageUrl}
        assistantName={resolvedNickname || 'Secretary'}
        isDeposit={spendIsDeposit}
      />
    </div>
  );
};

export default DreamieAvatarButton;
